import { Menu } from '@base-ui/react/menu'
import { Link, useMatchRoute, useNavigate, useRouter } from '@tanstack/react-router'
import { reconnect } from '@wagmi/core'
import { type Address } from 'ox'
import { type ComponentType, type PointerEvent, type ReactNode, useEffect } from 'react'
import { formatHash, Hash, Identicon } from 'regen-ui'
import { toast } from 'sonner'
import { useChainId, useConnection, useDisconnect } from 'wagmi'
import { tempo } from 'wagmi/chains'
import Activity from '~icons/lucide/activity'
import ArrowLeftRight from '~icons/lucide/arrow-left-right'
import Bot from '~icons/lucide/bot'
import Coins from '~icons/lucide/coins'
import Copy from '~icons/lucide/copy'
import CreditCard from '~icons/lucide/credit-card'
import Ellipsis from '~icons/lucide/ellipsis'
import Eye from '~icons/lucide/eye'
import House from '~icons/lucide/house'
import LifeBuoy from '~icons/lucide/life-buoy'
import LogOut from '~icons/lucide/log-out'
import ShieldCheck from '~icons/lucide/shield-check'
import SunMoon from '~icons/lucide/sun-moon'

import { checksumAddressForCopy } from '#/lib/address.js'
import * as Auth from '#/lib/auth.js'
import { wagmiConfig } from '#/lib/config.js'
import { readOnly } from '#/lib/connectors/readOnly.js'
import { useFundingOptions } from '#/lib/funding-options.js'
import { useTestnet } from '#/lib/testnet.js'
import * as Theme from '#/lib/theme.js'
import { MobileProfileSlotProvider } from '#/routes/_app/-components/MobileProfileSlot.js'
import { TempoLogo, TempoMark } from '#/ui/TempoLogo.js'

/**
 * Wallet layout: fixed sidebar + content column.
 *
 * - ≥1024px: full sidebar (260px) + content (max 1024px)
 * - 768–1023px: icon-only sidebar (68px) + content
 * - <768px: no sidebar, full-width content + bottom nav
 */
export function WithSidebar(props: WithSidebar.Props) {
  const router = useRouter()
  const address = Auth.useSessionAddress()
  const chainId = useChainId()
  const fundingOptions = useFundingOptions()
  const showCreditsActivity =
    !!address && chainId === tempo.id && fundingOptions.data?.coinflow === true

  // Eagerly preload sibling dashboard routes for snappy navigation.
  useEffect(() => {
    const routes = showCreditsActivity
      ? (['/balances', '/activity', '/credits/activity', '/apps'] as const)
      : (['/balances', '/activity', '/apps'] as const)
    for (const to of routes) router.preloadRoute({ to } as never).catch(() => {})
  }, [router, showCreditsActivity])

  return (
    <div className="relative flex min-h-dvh min-w-[360px] bg-background max-md:h-dvh max-md:flex-col max-md:overflow-hidden">
      <div className="flex-1 bg-background max-md:hidden" />
      <Sidebar showCreditsActivity={showCreditsActivity} />
      <main className="flex w-full max-w-[1024px] flex-col px-4 max-md:min-h-0 max-md:flex-1 max-md:overflow-y-auto sm:px-8">
        <MobileProfileSlotProvider value={<MobileProfile />}>
          {props.children}
        </MobileProfileSlotProvider>
      </main>
      <div className="flex-1 max-md:hidden" />
      <MobileNav showCreditsActivity={showCreditsActivity} />
      <StatusPills />
    </div>
  )
}

export declare namespace WithSidebar {
  type Props = {
    children: ReactNode
  }
}

const sidebar = [
  { exact: true, href: '/', icon: House, label: 'Home', mobileLabel: 'Home' },
  { exact: false, href: '/balances', icon: Coins, label: 'Balances', mobileLabel: 'Balances' },
  { exact: false, href: '/activity', icon: Activity, label: 'Activity', mobileLabel: 'Activity' },
  {
    auth: true,
    coinflow: true,
    exact: false,
    href: '/credits/activity',
    icon: CreditCard,
    label: 'Credits',
    mobileLabel: 'Credits',
  },
  {
    auth: true,
    exact: false,
    href: '/apps',
    icon: ShieldCheck,
    label: 'Authorized Apps',
    mobileLabel: 'Apps',
  },
] as const

function Sidebar(props: { showCreditsActivity: boolean }) {
  const address = Auth.useSessionAddress()

  return (
    <aside className="sticky top-0 flex h-dvh w-[260px] shrink-0 flex-col gap-6 border-r border-border bg-background px-2.5 pb-5 max-lg:w-[68px] max-lg:items-center max-lg:px-1.5 max-md:hidden">
      <Link
        aria-label="Home"
        className="-mb-5 inline-flex rounded-button px-3 pt-5 pb-3 focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-2 focus-visible:outline-focus"
        search={(prev: Record<string, unknown>) => prev}
        to="/"
      >
        <TempoLogo className="mt-2 h-4 w-auto max-lg:hidden" />
        <TempoMark className="mt-2 h-4 w-auto text-foreground lg:hidden" />
      </Link>
      <nav aria-label="Main" className="flex flex-1 flex-col gap-1">
        {sidebar.map((item) => {
          if ('auth' in item && item.auth && !address) return null
          if ('coinflow' in item && item.coinflow && !props.showCreditsActivity) return null
          return (
            <NavItem
              exact={item.exact}
              href={item.href}
              icon={item.icon}
              key={item.href}
              label={item.label}
            />
          )
        })}
      </nav>
      <ProfileCard />
    </aside>
  )
}

function ProfileCard() {
  const data = useProfile()
  if (!data) return null
  const { address, label, meLoading } = data

  return (
    <Menu.Root>
      <Menu.Trigger
        aria-label={`Account: ${label}`}
        className="relative flex items-center gap-3 rounded-button p-2.5 text-left hover:bg-surface-hover focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-2 focus-visible:outline-focus max-lg:justify-center max-lg:p-2"
      >
        <Identicon address={address} className="size-9 shrink-0" size={36} />
        {meLoading ? (
          <span className="flex-1 max-lg:hidden">
            <span className="block h-4 w-24 animate-pulse rounded-body bg-skeleton" />
          </span>
        ) : (
          <p
            aria-hidden="true"
            className="flex-1 truncate text-label-14 text-foreground max-lg:hidden"
          >
            {label}
          </p>
        )}
        <Ellipsis
          aria-hidden="true"
          className="size-[18px] shrink-0 text-foreground-secondary max-lg:absolute max-lg:right-1 max-lg:bottom-1 max-lg:size-4 max-lg:rounded-full max-lg:border max-lg:border-border max-lg:bg-surface"
        />
      </Menu.Trigger>
      <ProfilePopup data={data} side="top" />
    </Menu.Root>
  )
}

/** Mobile-only trigger that opens the same profile menu. */
function MobileProfile() {
  const data = useProfile()
  if (!data) return null
  const { address, label } = data

  return (
    <Menu.Root>
      <Menu.Trigger
        aria-label={`Account: ${label}`}
        className="flex size-9 items-center justify-center rounded-identicon bg-surface shadow-lg hover:bg-surface-hover focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-2 focus-visible:outline-focus"
      >
        <Identicon address={address} className="size-9 shrink-0" size={36} />
      </Menu.Trigger>
      <ProfilePopup data={data} side="bottom" />
    </Menu.Root>
  )
}

type ProfileData = {
  address: Address.Address
  dark: boolean
  isViewOnly: boolean
  label: string
  localTempoEnabled: boolean
  localnet: boolean
  meLoading: boolean
  onCopy: () => void
  onLogout: () => void
  onSwitchLocalnet: () => void
  onSwitchMainnet: () => void
  onToggleTestnet: () => void
  onToggleTheme: () => void
  testnet: boolean
}

function useProfile(): ProfileData | null {
  const me = Auth.me.useQuery()
  const router = useRouter()
  const navigate = useNavigate()
  const { address: connectedAddress, connector } = useConnection()
  const { disconnect } = useDisconnect()
  const {
    isLocalnet: localnet,
    isTestnet: testnet,
    localTempoEnabled,
    switchToLocalnet,
    switchToMainnet,
    toggle: toggleTestnet,
  } = useTestnet()
  const isViewOnly = connector?.type === readOnly.type
  const [theme] = Theme.useTheme()
  const dark = (() => {
    if (theme !== 'system') return theme === 'dark'
    return (
      typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
    )
  })()
  const logout = Auth.logout.useMutation({
    onSuccess: async () => {
      await router.invalidate()
    },
  })

  const address = connectedAddress as Address.Address | undefined
  if (!address) return null

  const meLoading = !isViewOnly && me.isLoading
  const label = (() => {
    if (isViewOnly) return formatHash(address)
    if (me.data) return me.data.email ?? me.data.username ?? formatHash(address)
    return formatHash(address)
  })()

  return {
    address,
    dark,
    isViewOnly,
    label,
    localTempoEnabled,
    localnet,
    meLoading,
    onCopy: () => {
      navigator.clipboard.writeText(checksumAddressForCopy(address))
      toast.success('Address copied')
    },
    onLogout: async () => {
      if (isViewOnly) {
        disconnect()
        await reconnect(wagmiConfig)
        navigate({ to: '/', search: {} })
      } else logout.mutate()
    },
    onSwitchLocalnet: switchToLocalnet,
    onSwitchMainnet: switchToMainnet,
    onToggleTestnet: toggleTestnet,
    onToggleTheme: () => Theme.set(dark ? 'light' : 'dark'),
    testnet,
  }
}

function ProfilePopup(props: { data: ProfileData; side: 'top' | 'bottom' }) {
  const { data, side } = props
  const {
    address,
    dark,
    isViewOnly,
    localTempoEnabled,
    localnet,
    onCopy,
    onLogout,
    onSwitchLocalnet,
    onSwitchMainnet,
    onToggleTestnet,
    onToggleTheme,
    testnet,
  } = data

  return (
    <Menu.Portal>
      <Menu.Positioner
        align={side === 'top' ? 'start' : 'end'}
        className="z-50"
        side={side}
        sideOffset={8}
      >
        <Menu.Popup className="w-[240px] rounded-body border border-border bg-surface p-1 shadow-[0_4px_6px_-1px_rgb(0_0_0/0.04),0_2px_4px_-2px_rgb(0_0_0/0.04)]">
          <Menu.Item
            className="flex cursor-default items-center gap-3 rounded-button py-2.5 pl-3 pr-1.5 outline-none data-highlighted:bg-surface-hover"
            onClick={onCopy}
          >
            <Copy aria-hidden="true" className="size-4 shrink-0 text-foreground-secondary" />
            <div className="flex-1">
              <p className="text-label-14 text-foreground">Copy address</p>
              <p className="text-label-13 text-foreground-secondary">
                <Hash value={address} />
              </p>
            </div>
            <Kbd keys={['⌥', 'A']} />
          </Menu.Item>
          <Menu.Item
            className="flex cursor-default items-center gap-3 rounded-button py-2.5 pl-3 pr-1.5 outline-none data-highlighted:bg-surface-hover"
            onClick={onToggleTestnet}
          >
            <ArrowLeftRight
              aria-hidden="true"
              className="size-4 shrink-0 text-foreground-secondary"
            />
            <p className="flex-1 text-label-14 text-foreground">
              {testnet ? 'Switch to Mainnet' : 'Switch to Testnet'}
            </p>
            <Kbd keys={['⌥', 'T']} />
          </Menu.Item>
          {localTempoEnabled && (
            <Menu.Item
              className="flex cursor-default items-center gap-3 rounded-button py-2.5 pl-3 pr-1.5 outline-none data-highlighted:bg-surface-hover"
              onClick={localnet ? onSwitchMainnet : onSwitchLocalnet}
            >
              <ArrowLeftRight
                aria-hidden="true"
                className="size-4 shrink-0 text-foreground-secondary"
              />
              <p className="flex-1 text-label-14 text-foreground">
                {localnet ? 'Switch to Mainnet' : 'Switch to Localnet'}
              </p>
            </Menu.Item>
          )}
          <Menu.Item
            className="flex cursor-default items-center gap-3 rounded-button py-2.5 pl-3 pr-1.5 outline-none data-highlighted:bg-surface-hover"
            onClick={onToggleTheme}
          >
            <SunMoon aria-hidden="true" className="size-4 shrink-0 text-foreground-secondary" />
            <p className="flex-1 text-label-14 text-foreground">
              {dark ? 'Light mode' : 'Dark mode'}
            </p>
            <Kbd keys={['⌥', 'M']} />
          </Menu.Item>
          <Menu.Item
            className="flex cursor-default items-center gap-3 rounded-button px-3 py-2.5 outline-none data-highlighted:bg-surface-hover"
            render={<a href="/agent" />}
          >
            <Bot aria-hidden="true" className="size-4 shrink-0 text-foreground-secondary" />
            <p className="text-label-14 text-foreground">Agent payments</p>
          </Menu.Item>
          <Menu.Item
            className="flex cursor-default items-center gap-3 rounded-button px-3 py-2.5 outline-none data-highlighted:bg-surface-hover"
            render={<a href="/support" rel="noopener noreferrer" target="_blank" />}
          >
            <LifeBuoy aria-hidden="true" className="size-4 shrink-0 text-foreground-secondary" />
            <p className="text-label-14 text-foreground">Support</p>
          </Menu.Item>
          <Menu.Item
            className="flex cursor-default items-center gap-3 rounded-button px-3 py-2.5 outline-none data-highlighted:bg-surface-hover"
            onClick={onLogout}
          >
            <LogOut aria-hidden="true" className="size-4 shrink-0 text-negative" />
            <p className="text-label-14 text-negative">
              {isViewOnly ? 'Exit impersonating' : 'Log out'}
            </p>
          </Menu.Item>
        </Menu.Popup>
      </Menu.Positioner>
    </Menu.Portal>
  )
}

function NavItem(props: NavItem.Props) {
  const { exact, href, icon: Icon, label } = props
  const matchRoute = useMatchRoute()
  const navigate = useNavigate()
  const active = !!matchRoute({ to: href, fuzzy: !exact })
  const onPress = (event: PointerEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey)
      return
    if (active) return
    navigate({ to: href, search: (prev: Record<string, unknown>) => prev } as never)
  }

  return (
    <Link
      aria-current={active ? 'page' : undefined}
      aria-label={label}
      className="flex items-center gap-3 rounded-button px-3 py-3 text-label-16 text-foreground-secondary hover:bg-surface-hover data-active:text-foreground data-active:font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus max-lg:justify-center max-lg:gap-0 max-lg:p-2.5"
      data-active={active || undefined}
      onPointerDown={onPress}
      search={(prev: Record<string, unknown>) => prev}
      to={href}
    >
      <Icon aria-hidden="true" className="size-5" />
      <span className="max-lg:hidden">{label}</span>
    </Link>
  )
}

function MobileNav(props: { showCreditsActivity: boolean }) {
  const matchRoute = useMatchRoute()
  const navigate = useNavigate()
  const { connector } = useConnection()
  const isViewOnly = connector?.type === readOnly.type

  return (
    <nav
      aria-label="Main"
      className="z-10 flex w-full shrink-0 items-center justify-around border-t border-border bg-background pb-[env(safe-area-inset-bottom)] md:hidden"
    >
      {sidebar.map((item) => {
        if ('auth' in item && item.auth && isViewOnly) return null
        if ('coinflow' in item && item.coinflow && !props.showCreditsActivity) return null
        const active = !!matchRoute({ to: item.href as never, fuzzy: !item.exact })
        const onPress = (event: PointerEvent<HTMLAnchorElement>) => {
          if (
            event.button !== 0 ||
            event.metaKey ||
            event.altKey ||
            event.ctrlKey ||
            event.shiftKey
          )
            return
          if (active) return
          navigate({ to: item.href, search: (prev: Record<string, unknown>) => prev } as never)
        }

        return (
          <Link
            aria-current={active ? 'page' : undefined}
            aria-label={item.label}
            className="flex flex-1 flex-col items-center gap-1 py-3 text-foreground-secondary data-active:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
            data-active={active || undefined}
            key={item.href}
            onPointerDown={onPress}
            search={((prev: Record<string, unknown>) => prev) as never}
            to={item.href as never}
          >
            <item.icon aria-hidden="true" className="size-5" />
            <span className="text-label-12">{item.mobileLabel}</span>
          </Link>
        )
      })}
    </nav>
  )
}

function Kbd(props: { keys: readonly string[] }) {
  return (
    <kbd className="ml-auto inline-flex items-center gap-px rounded-button border border-border bg-secondary px-1 py-px text-[10px] leading-tight text-foreground-secondary">
      {props.keys.map((key) => (
        <span key={key}>{key}</span>
      ))}
    </kbd>
  )
}

function StatusPills() {
  const { connector, address } = useConnection()
  const { disconnect } = useDisconnect()
  const {
    isLocalnet: localnet,
    isTestnet: testnet,
    switchToMainnet,
    toggle: toggleTestnet,
  } = useTestnet()
  const navigate = useNavigate()

  const isViewOnly = connector?.type === readOnly.type

  if (!isViewOnly && !localnet && !testnet) return null

  return (
    <div className="fixed bottom-4 right-4 flex items-center gap-2 max-md:bottom-20">
      {isViewOnly && (
        <div className="flex items-center gap-2 rounded-body border border-border bg-surface py-1.5 pl-3 pr-1.5 text-label-13 text-foreground-secondary shadow-lg">
          <Eye className="size-3.5" />
          {address && (
            <span className="inline-flex items-center gap-1">
              Viewing <Hash value={address} />
            </span>
          )}
          <button
            className="rounded-button bg-secondary px-2.5 py-0.5 text-foreground transition-colors hover:bg-secondary-hover"
            onClick={async () => {
              disconnect()
              await reconnect(wagmiConfig)
              navigate({ to: '/', search: {} })
            }}
            type="button"
          >
            Exit
          </button>
        </div>
      )}
      {localnet && (
        <div className="flex items-center gap-2 rounded-body border border-border bg-surface py-1.5 pl-3 pr-1.5 text-label-13 text-foreground-secondary shadow-lg">
          <span className="size-1.5 rounded-full bg-current text-positive shadow-[0_0_3px_1px_var(--text-color-positive)]" />
          <span>Localnet</span>
          <button
            className="rounded-button bg-secondary px-2.5 py-0.5 text-foreground transition-colors hover:bg-secondary-hover"
            onClick={switchToMainnet}
            type="button"
          >
            Switch to Mainnet
          </button>
        </div>
      )}
      {testnet && (
        <div className="flex items-center gap-2 rounded-body border border-border bg-surface py-1.5 pl-3 pr-1.5 text-label-13 text-foreground-secondary shadow-lg">
          <span className="size-1.5 rounded-full bg-current text-warning shadow-[0_0_3px_1px_var(--text-color-warning)]" />
          <span>Testnet</span>
          <button
            className="rounded-button bg-secondary px-2.5 py-0.5 text-foreground transition-colors hover:bg-secondary-hover"
            onClick={toggleTestnet}
            type="button"
          >
            Switch to Mainnet
          </button>
        </div>
      )}
    </div>
  )
}

declare namespace NavItem {
  type Props = {
    exact: boolean
    href: string
    icon: ComponentType<{ className?: string }>
    label: string
  }
}
